// src/pages/Library.js
import React, { useEffect, useState, useMemo } from "react";
import { io } from "socket.io-client";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import FolderCard from "../../components/User/FolderCard";
import AddFolderModal from "../../components/User/AddFolderModal";
import NoteCard from "../../components/NoteCard"; 
import SwiftAlert from "../../components/SwiftAlert"; 
import { useLibraryData } from "../../hooks/useLibraryData";
import { API_URL, createFolder, updateReviewer, updateFolder, deleteReviewer, deleteFolder, getReviewerById, redeemSharedCode, getUserArchivedReviewers } from "../../services/api"; 
import ShareModal from "../../components/User/ShareModal";
import { checkEnabled, normalizeColorKey } from "../../utils/libraryUtils";
import { generatePDFDocument } from "../../utils/pdfGenerator";

// --- Theme Mapping for Modal List Items ---
const LIST_ITEM_VARIANTS = {
  blue:    { iconBg: "bg-blue-100",    iconText: "text-blue-600",    activeBorder: "border-blue-500",    activeBg: "bg-blue-50",    ring: "ring-blue-500" },
  orange:  { iconBg: "bg-orange-100",  iconText: "text-orange-600",  activeBorder: "border-orange-500",  activeBg: "bg-orange-50",  ring: "ring-orange-500" },
  purple:  { iconBg: "bg-purple-100",  iconText: "text-purple-600",  activeBorder: "border-purple-500",  activeBg: "bg-purple-50",  ring: "ring-purple-500" },
  emerald: { iconBg: "bg-emerald-100", iconText: "text-emerald-600", activeBorder: "border-emerald-500", activeBg: "bg-emerald-50", ring: "ring-emerald-500" },
  red:     { iconBg: "bg-red-100",     iconText: "text-red-600",     activeBorder: "border-red-500",     activeBg: "bg-red-50",     ring: "ring-red-500" },
  gray:    { iconBg: "bg-slate-100",   iconText: "text-slate-600",   activeBorder: "border-slate-500",   activeBg: "bg-slate-50",   ring: "ring-slate-500" },
};

const Library = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  // -- Folder Modal States --
  const [isModalOpen, setIsModalOpen] = useState(false); // Create Folder
  const [isRenameFolderModalOpen, setIsRenameFolderModalOpen] = useState(false); // Rename Folder
  const [isDeleteFolderModalOpen, setIsDeleteFolderModalOpen] = useState(false); // ✅ Delete Folder State
  
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [folderToDelete, setFolderToDelete] = useState(null); // ✅ Folder to Delete
  const [renameFolderInput, setRenameFolderInput] = useState("");

  // -- Note Action States --
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // Note Delete State
  const [noteToMove, setNoteToMove] = useState(null);
  const [noteToDelete, setNoteToDelete] = useState(null); // Note to Delete
  const [isSharedCodeModalOpen, setIsSharedCodeModalOpen] = useState(false);
  const [sharedCodeInput, setSharedCodeInput] = useState("");
  const [isRedeemingCode, setIsRedeemingCode] = useState(false);
  // Share Modal
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [noteToShare, setNoteToShare] = useState(null);

  // -- Filter & Grouping & Inline Archive States --
  const [selectedFolder, setSelectedFolder] = useState("all");
  const [groupByFolder, setGroupByFolder] = useState(false);
  const [collapsedFolders, setCollapsedFolders] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [archivedNotes, setArchivedNotes] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);

  // -- Alert State --
  const [alert, setAlert] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  const { folders, recentNotes, loading, addFolderToState, setRecentNotes, setFolders, markNoteAccessed } = useLibraryData();

  // Auto-archive notes beyond the 15-note limit (oldest first)
  useEffect(() => {
    if (!loading && recentNotes.length > 15) {
      const toArchive = recentNotes.slice(15);
      toArchive.forEach(async (note) => {
        try {
          await updateReviewer(note._id, { archived: true });
        } catch (e) {
          console.error("[Library] Auto-archive failed for", note._id, e.message);
        }
      });
      setRecentNotes(recentNotes.slice(0, 15));
    }
  }, [loading, recentNotes, setRecentNotes]);

  useEffect(() => {
    const socketUrl = API_URL.replace(/\/api$/, "");
    const token = localStorage.getItem("jwt_token");
    const socket = io(socketUrl, {
      withCredentials: true,
      path: "/api/socket.io",
      auth: token ? { token } : undefined,
    });

    socket.on("reviewer_shared", ({ reviewer }) => {
      if (reviewer?._id) {
        setRecentNotes((previous) => {
          if (previous.some((note) => String(note._id || note.id) === String(reviewer._id))) {
            return previous;
          }
          return [{ ...reviewer, verified: false }, ...previous];
        });
      }
    });

    return () => socket.disconnect();
  }, [setRecentNotes]);

  // Search Query
  const q = useMemo(() => {
    return (new URLSearchParams(location.search).get("q") || "").toLowerCase().trim();
  }, [location.search]);

  // Derived Data: Folder Map (ID -> Name)
  const folderMap = useMemo(() => {
    const map = {};
    if (Array.isArray(folders)) {
      folders.forEach((f) => {
        if (f._id) map[f._id] = f.name;
        if (f.id) map[f.id] = f.name;
      });
    }
    return map;
  }, [folders]);

  // Derived Data: Filtering
  const filteredFolders = useMemo(() => {
    if (!q) return folders;
    return folders.filter((f) => (f.name || "").toLowerCase().includes(q));
  }, [folders, q]);

  const searching = Boolean(q);

  // Deduplicated notes to protect against any duplicate records
  const uniqueRecentNotes = useMemo(() => {
    const seen = new Set();
    return recentNotes.filter((n) => {
      const id = String(n._id || n.id);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [recentNotes]);

  // Set of note IDs currently displayed in Recent Activity (top 8 notes)
  const recentNoteIds = useMemo(() => {
    if (searching) return new Set();
    return new Set(uniqueRecentNotes.slice(0, 8).map((n) => String(n._id || n.id)));
  }, [uniqueRecentNotes, searching]);

  // Notes displayed in Reviewer Notes section (excludes recent activity notes when not searching)
  const reviewerSectionNotes = useMemo(() => {
    if (searching) return uniqueRecentNotes;
    return uniqueRecentNotes.filter((n) => !recentNoteIds.has(String(n._id || n.id)));
  }, [uniqueRecentNotes, recentNoteIds, searching]);

  const filteredNotes = useMemo(() => {
    let notes = reviewerSectionNotes;
    if (q) {
      return notes.filter((n) => {
        const title = (n.title || n.topic || "").toLowerCase();
        const subject = (n.meta || "").toLowerCase();
        const fName = (folderMap[n.folder] || folderMap[n.folder?._id] || "").toLowerCase();
        return title.includes(q) || subject.includes(q) || fName.includes(q);
      });
    }
    if (selectedFolder !== "all") {
      notes = notes.filter((n) => {
        const fName = folderMap[n.folder] || folderMap[n.folder?._id] || "Uncategorized";
        return fName === selectedFolder;
      });
    }
    return notes;
  }, [reviewerSectionNotes, q, folderMap, selectedFolder]);

  // Derived: Notes grouped by Folder
  const groupedNotes = useMemo(() => {
    const groups = {};
    filteredNotes.forEach((n) => {
      const fName = folderMap[n.folder] || folderMap[n.folder?._id] || "Uncategorized";
      if (!groups[fName]) {
        groups[fName] = [];
      }
      groups[fName].push(n);
    });
    return groups;
  }, [filteredNotes, folderMap]);

  // Derived: Available folder names from reviewerSectionNotes
  const availableNoteFolders = useMemo(() => {
    const set = new Set();
    reviewerSectionNotes.forEach((n) => {
      const name = folderMap[n.folder] || folderMap[n.folder?._id];
      if (name) set.add(name);
    });
    return Array.from(set).sort();
  }, [reviewerSectionNotes, folderMap]);

  // --- HANDLERS: FOLDERS ---

  const handleAddFolder = async (folderData) => {
    try {
      const newFolder = await createFolder(folderData);
      addFolderToState(newFolder);
      setAlert({ type: 'success', title: 'Success', message: 'Folder created successfully.' });
      return true;
    } catch (error) {
      if (error?.response?.status === 409) {
        setAlert({ type: 'error', title: 'Folder already exist', message: '' });
        return false;
      }
      setAlert({ type: 'error', title: 'Unable to create folder', message: error?.response?.data?.error || 'Please try again.' });
      return false;
    }
  };

  const handleOpenRenameFolder = (folder) => {
    setFolderToEdit(folder);
    setRenameFolderInput(folder.name);
    setIsRenameFolderModalOpen(true);
  };

  const handleRenameFolderSubmit = async () => {
    const cleanName = renameFolderInput.trim();
    if (!folderToEdit || !cleanName) return;

    try {
      const updatedFolders = folders.map(f => 
        (f._id === folderToEdit._id) ? { ...f, name: cleanName } : f
      );
      if (setFolders) setFolders(updatedFolders);

      setIsRenameFolderModalOpen(false);
      setAlert({ type: 'success', title: 'Success', message: 'Folder renamed.' });

      await updateFolder(folderToEdit._id, { name: cleanName });
    } catch (error) {
      console.error(error);
      setAlert({ type: 'error', title: 'Failed', message: error?.response?.data?.error || 'Failed to rename folder.' });
    }
  };

  const handleFolderColorChange = async (folderId, newColor) => {
    try {
      const updatedFolders = folders.map(f => 
        (f._id === folderId) ? { ...f, color: newColor } : f
      );
      if (setFolders) setFolders(updatedFolders);

      await updateFolder(folderId, { color: newColor });
    } catch (error) {
      console.error(error);
      setAlert({ type: 'error', title: 'Failed', message: 'Failed to update folder color.' });
    }
  };

  // ✅ NEW: Folder Delete Handlers
  const openDeleteFolderModal = (folder) => {
    setFolderToDelete(folder);
    setIsDeleteFolderModalOpen(true);
  };

  const handleDeleteFolderSubmit = async () => {
    if (!folderToDelete) return;

    try {
      // 1. Optimistic Update: Remove folder from list
      const updatedFolders = folders.filter(f => f._id !== folderToDelete._id);
      if (setFolders) setFolders(updatedFolders);

      setIsDeleteFolderModalOpen(false);
      setFolderToDelete(null);
      setAlert({ type: 'success', title: 'Deleted', message: 'Folder deleted successfully.' });

      // 2. API Call
      await deleteFolder(folderToDelete._id);
    } catch (error) {
      console.error("Delete folder error:", error);
      setAlert({ type: 'error', title: 'Failed', message: 'Failed to delete folder.' });
      // Ideally rollback state here if API fails
    }
  };

  // Archive Folder Handler
  const handleArchiveFolder = async (folder) => {
    try {
      const updatedFolders = folders.filter(f => f._id !== folder._id);
      if (setFolders) setFolders(updatedFolders);
      setRecentNotes(prev => prev.filter(n => {
        const noteFolderId = n.folder?._id || n.folder;
        return String(noteFolderId) !== String(folder._id);
      }));

      setAlert({ type: 'success', title: 'Archived', message: `Folder "${folder.name}" and its notes were moved to archive.` });

      await updateFolder(folder._id, { archived: true });
    } catch (error) {
      console.error("Archive folder error:", error);
      setAlert({ type: 'error', title: 'Failed', message: 'Failed to archive folder.' });
    }
  };

  // --- HANDLERS: NOTES ---

  const handleRenameNote = async (note, newTitle) => {
    const cleanTitle = newTitle.trim();
    if (!cleanTitle) return;

    const isDuplicate = recentNotes.some(n => 
      (n.title || n.topic || "").trim().toLowerCase() === cleanTitle.toLowerCase() &&
      n._id !== note._id 
    );

    if (isDuplicate) {
      setAlert({
        type: "warning",
        title: "Name Taken",
        message: `A note named "${cleanTitle}" already exists.`
      });
      return;
    }

    try {
      const updatedNotes = recentNotes.map(n => 
        (n._id === note._id) ? { ...n, title: cleanTitle, topic: cleanTitle } : n
      );
      setRecentNotes(updatedNotes); 

      await updateReviewer(note._id, { title: cleanTitle });
      setAlert({ type: 'success', title: 'Renamed', message: 'Note renamed successfully.' });
    } catch (error) {
      console.error(error);
      setAlert({
        type: 'error',
        title: 'Failed',
        message: error?.message || 'Failed to rename note.',
      });
    }
  };

  const handleColorChange = async (note, newColor) => {
    try {
      const updatedNotes = recentNotes.map(n => 
        (n._id === note._id) ? { ...n, color: newColor } : n
      );
      setRecentNotes(updatedNotes);

      await updateReviewer(note._id, { color: newColor });
    } catch (error) {
      console.error(error);
      setAlert({ type: 'error', title: 'Failed', message: 'Failed to update color.' });
    }
  };

  // -- Move Logic --
  const openMoveModal = (note) => {
    setNoteToMove(note);
    setIsMoveModalOpen(true);
  };

  const handleMoveSubmit = async (targetFolderId) => {
    if (!noteToMove) return;
    const sourceFolderId = noteToMove.folder?._id || noteToMove.folder;

    try {
      // 1. Update Notes List
      const updatedNotes = recentNotes.map(n => 
        (n._id === noteToMove._id) ? { ...n, folder: targetFolderId } : n
      );
      setRecentNotes(updatedNotes);

      // 2. Update Folder Counts
      if (setFolders) {
        const updatedFolders = folders.map(f => {
          if (f._id === sourceFolderId) return { ...f, count: Math.max(0, f.count - 1) };
          if (f._id === targetFolderId) return { ...f, count: f.count + 1 };
          return f;
        });
        setFolders(updatedFolders);
      }
      
      setIsMoveModalOpen(false);
      setNoteToMove(null);
      setAlert({ type: 'success', title: 'Moved', message: 'Note moved successfully.' });

      await updateReviewer(noteToMove._id, { folderId: targetFolderId });
    } catch (error) {
      console.error(error);
      setAlert({ type: 'error', title: 'Failed', message: 'Failed to move note.' });
    }
  };

  // -- DELETE Note Logic --
  const openDeleteModal = (note) => {
    setNoteToDelete(note);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteSubmit = async () => {
    if (!noteToDelete) return;
    const sourceFolderId = noteToDelete.folder?._id || noteToDelete.folder;

    try {
      // 1. Optimistic UI Update: Remove from list
      const updatedNotes = recentNotes.filter(n => n._id !== noteToDelete._id);
      setRecentNotes(updatedNotes);

      // 2. Optimistic UI Update: Decrement Folder Count (if applicable)
      if (sourceFolderId && setFolders) {
        const updatedFolders = folders.map(f => {
          if (f._id === sourceFolderId) return { ...f, count: Math.max(0, f.count - 1) };
          return f;
        });
        setFolders(updatedFolders);
      }

      setIsDeleteModalOpen(false);
      setNoteToDelete(null);
      setAlert({ type: 'success', title: 'Deleted', message: 'Note deleted permanently.' });

      // 3. API Call
      await deleteReviewer(noteToDelete._id);

    } catch (error) {
      console.error("Delete error:", error);
      setAlert({ type: 'error', title: 'Failed', message: 'Failed to delete note.' });
    }
  };

  const handleStudyNavigation = (file, requestedMode) => {
    const fileId = file?._id || file?.id;
    if (fileId && typeof markNoteAccessed === "function") {
      markNoteAccessed(fileId);
    }

    const cfg = file?.config || {};
    let mode = requestedMode;

    if (!mode) {
      if (checkEnabled(cfg.summaryEnabled)) mode = "summary";
      else if (checkEnabled(cfg.flashcardsEnabled)) mode = "flashcards";
      else if (checkEnabled(cfg.quizEnabled)) mode = "quiz";
    }
    if (!mode) return;

    const noteData = {
      ...file,
      _id: file?._id,
      id: file?._id || file?.id,
      title: file?.title || file?.topic || "Untitled",
      subject: file?.meta || "Library",
      hasSummary: checkEnabled(cfg.summaryEnabled),
      hasFlashcards: checkEnabled(cfg.flashcardsEnabled),
      hasQuiz: checkEnabled(cfg.quizEnabled),
      config: cfg,
    };

    navigate("/study-mode", { 
      state: { 
        mode, 
        noteData, 
        reviewerId: file?._id || file?.id 
      } 
    });
  };

  const handleDownloadPdf = async (note) => {
    const reviewerId = note?._id || note?.id;
    if (!reviewerId) {
      setAlert({ type: "error", title: "Failed", message: "Reviewer ID is missing." });
      return;
    }

    try {
      const raw = await getReviewerById(reviewerId);
      const normalized = raw && raw.data && raw.status ? raw.data : raw;
      const meta = normalized?.meta || normalized?.data?.meta || {};
      const data =
        normalized?.meta && normalized?.data
          ? normalized.data
          : normalized?.data?.data ?? normalized?.data ?? normalized;

      const summary =
        (typeof data === "string" ? data : "") ||
        data?.summaryHtml ||
        data?.summary ||
        data?.html ||
        data?.content ||
        "";

      const flashcardsRaw = data?.flashcards || data?.flashCards || data?.cards || [];
      const flashcards = Array.isArray(flashcardsRaw)
        ? flashcardsRaw
            .map((c) => ({
              front: c?.front || c?.term || c?.question || c?.q || c?.title || "",
              back: c?.back || c?.definition || c?.answer || c?.a || c?.content || "",
            }))
            .filter((c) => c.front || c.back)
        : [];

      const quizRaw = data?.quiz || data?.questions || [];
      const quiz = Array.isArray(quizRaw)
        ? quizRaw
            .map((q) => {
              const optsRaw = q?.options || q?.choices || q?.answers || [];
              const options = Array.isArray(optsRaw)
                ? optsRaw
                    .map((o) => (typeof o === "string" ? o : o?.text || o?.label || ""))
                    .filter(Boolean)
                : [];
              return {
                question: q?.question || q?.prompt || q?.q || "",
                options,
                answer: q?.answer || q?.correctAnswer || q?.correct || "",
                explanation: q?.explanation || q?.reason || "",
              };
            })
            .filter((q) => q.question)
        : [];

      if (!summary && flashcards.length === 0 && quiz.length === 0) {
        setAlert({ type: "warning", title: "No Content", message: "This reviewer has no downloadable content yet." });
        return;
      }

      const cfg = { ...(meta?.config || {}), ...(note?.config || {}) };
      generatePDFDocument({
        title: note?.title || note?.topic || meta?.topic || "Reviewer",
        generatedData: { summary, flashcards, quiz },
        config: {
          ...cfg,
          summaryEnabled: checkEnabled(cfg.summaryEnabled),
          flashcardsEnabled: checkEnabled(cfg.flashcardsEnabled),
          quizEnabled: checkEnabled(cfg.quizEnabled),
        },
      });
    } catch (error) {
      setAlert({
        type: "error",
        title: "Failed",
        message: error?.message || "Failed to download PDF.",
      });
    }
  };

  const handleSharedCodeSubmit = async () => {
    if (isRedeemingCode) return;

    const code = sharedCodeInput.trim().toUpperCase();
    if (!code) {
      setAlert({
        type: "warning",
        title: "Code Required",
        message: "Please enter a shared code.",
      });
      return;
    }

    try {
      setIsRedeemingCode(true);
      const res = await redeemSharedCode(code);
      const sharedReviewer = res?.reviewer || res?.data || null;

      if (sharedReviewer) {
        const sharedId = String(sharedReviewer._id || sharedReviewer.id);
        setRecentNotes((prev) => {
          if (prev.some((n) => String(n._id || n.id) === sharedId)) {
            return prev;
          }
          return [sharedReviewer, ...prev];
        });
      }

      setAlert({
        type: res?.alreadyOwned ? "info" : "success",
        title: res?.alreadyOwned ? "Reviewer Already Owned" : "Shared Reviewer Added",
        message: res?.alreadyOwned
          ? "You created this reviewer; it is already in your library."
          : "The reviewer was added to your library and marked as shared.",
      });
      setIsSharedCodeModalOpen(false);
      setSharedCodeInput("");
    } catch (error) {
      setAlert({
        type: "error",
        title: "Unable to Redeem",
        message: error?.message || "Failed to redeem shared code.",
      });
    } finally {
      setIsRedeemingCode(false);
    }
  };

  // Share Modal handlers
  const openShareModal = (note) => {
    setNoteToShare(note);
    setIsShareModalOpen(true);
  };

  const handleShareModalUpdateNote = (updatedNote) => {
    setRecentNotes((prev) =>
      prev.map((n) => ((n._id || n.id) === (updatedNote._id || updatedNote.id) ? updatedNote : n))
    );
  };

  // Archive handler
  const handleArchiveNote = async (note) => {
    try {
      setRecentNotes((prev) => prev.filter((n) => n._id !== note._id));
      await updateReviewer(note._id, { archived: true });
      setAlert({ type: 'success', title: 'Archived', message: `"${note.title || note.topic}" moved to archive.` });
      // If inline archived view is open, refresh or append
      if (showArchived) {
        setArchivedNotes((prev) => [{ ...note, archived: true }, ...prev]);
      }
    } catch (error) {
      setAlert({ type: 'error', title: 'Failed', message: 'Failed to archive note.' });
    }
  };

  // Inline Unarchive handler
  const handleUnarchiveNote = async (note) => {
    try {
      setArchivedNotes((prev) => prev.filter((n) => (n._id || n.id) !== (note._id || note.id)));
      setRecentNotes((prev) => [{ ...note, archived: false }, ...prev]);
      await updateReviewer(note._id || note.id, { archived: false });
      setAlert({ type: 'success', title: 'Unarchived', message: `"${note.title || note.topic}" restored to Library.` });
    } catch (error) {
      setAlert({ type: 'error', title: 'Failed', message: 'Failed to unarchive note.' });
    }
  };

  // Toggle Show Archived Inline
  const handleToggleShowArchived = async () => {
    const nextState = !showArchived;
    setShowArchived(nextState);
    if (nextState && archivedNotes.length === 0) {
      setArchivedLoading(true);
      try {
        const res = await getUserArchivedReviewers();
        const data = Array.isArray(res) ? res : res?.data || [];
        setArchivedNotes(data);
      } catch (err) {
        console.error("Failed to load archived notes", err);
        setAlert({ type: 'error', title: 'Error', message: 'Failed to load archived notes.' });
      } finally {
        setArchivedLoading(false);
      }
    }
  };

  // Toggle folder group collapse
  const toggleFolderCollapse = (folderName) => {
    setCollapsedFolders((prev) => ({
      ...prev,
      [folderName]: !prev[folderName],
    }));
  };

  return (
    <div className="bg-slate-50 text-slate-800 flex h-screen overflow-hidden font-sans antialiased">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <Header toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-7xl mx-auto w-full animate-fade-in pb-20 md:pb-0">
            
            {/* Overview Banner */}
            <div 
              role="region" 
              aria-label="Library Overview and Quick Actions"
              className="w-full bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-700 rounded-2xl p-6 md:p-8 text-white shadow-xl mb-8 relative overflow-hidden"
            >
              <div className="absolute right-0 top-0 h-full w-1/3 bg-white/10 skew-x-12 transform translate-x-12 pointer-events-none"></div>
              <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div>
                  <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-blue-500/30 text-blue-100 text-xs font-semibold tracking-wide uppercase mb-2 border border-blue-400/30">
                    <i className="fa-solid fa-graduation-cap text-blue-200"></i> My Study Workspace
                  </div>
                  <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white mb-2">Library Overview</h1>
                  <p className="text-blue-100 text-sm md:text-base max-w-xl">
                    {loading ? "Fetching your study materials..." : searching 
                      ? `Displaying results matching "${q}"` 
                      : `Organize your notes across ${folders.length} subject${folders.length === 1 ? '' : 's'} and ${recentNotes.length} reviewer note${recentNotes.length === 1 ? '' : 's'}.`}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => setIsSharedCodeModalOpen(true)}
                    aria-label="Enter shared study reviewer code"
                    className="bg-blue-800/60 hover:bg-blue-800 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition shadow-sm flex items-center gap-2 border border-blue-400/30 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none"
                  >
                    <i className="fa-solid fa-share-nodes text-blue-300" aria-hidden="true"></i> Enter Code
                  </button>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    aria-label="Create new subject folder"
                    className="bg-white text-blue-800 hover:bg-blue-50 px-5 py-2.5 rounded-xl font-extrabold text-sm transition shadow-md flex items-center gap-2 focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:outline-none"
                  >
                    <i className="fa-solid fa-folder-plus text-blue-700" aria-hidden="true"></i> New Folder
                  </button>
                  <button
                    onClick={() => navigate('/library/archive')}
                    aria-label="Open archived notes page"
                    className="bg-blue-800/60 hover:bg-blue-800 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition shadow-sm flex items-center gap-2 border border-blue-400/30 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none"
                  >
                    <i className="fa-solid fa-box-archive text-blue-300" aria-hidden="true"></i> Archive
                  </button>
                </div>
              </div>
            </div>

            {/* Subjects Grid */}
            <section className="mb-10" aria-labelledby="subjects-heading">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold shadow-xs">
                    <i className="fa-solid fa-folder-tree" aria-hidden="true"></i>
                  </span>
                  <div>
                    <h2 id="subjects-heading" className="text-base md:text-lg font-extrabold text-slate-900 tracking-tight">
                      Subjects &amp; Courses
                    </h2>
                    <p className="text-xs text-slate-500">Group your reviewers by class or topic</p>
                  </div>
                </div>
                <span className="text-xs font-bold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-full">
                  {filteredFolders.length} {filteredFolders.length === 1 ? 'Course' : 'Courses'}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {loading ? (
                  [...Array(4)].map((_, i) => <div key={i} className="h-32 bg-white rounded-2xl border border-gray-100 animate-pulse shadow-sm" />)
                ) : (
                  <>
                    {filteredFolders.map((folder) => (
                      <FolderCard
                        key={folder._id}
                        name={folder.name}
                        count={folder.count}
                        color={folder.color}
                        onClick={() => navigate(`/library/${folder._id}`, { state: { folder } })}
                        onRename={() => handleOpenRenameFolder(folder)}
                        onColorChange={(color) => handleFolderColorChange(folder._id, color)}
                        onDelete={() => openDeleteFolderModal(folder)}
                        onArchive={() => handleArchiveFolder(folder)}
                      />
                    ))}
                    {!searching && (
                      <button
                        onClick={() => setIsModalOpen(true)}
                        aria-label="Add new subject or course"
                        className="border-2 border-dashed border-blue-300 bg-blue-50/40 p-5 rounded-2xl cursor-pointer hover:border-blue-600 hover:bg-blue-50 transition flex flex-col items-center justify-center text-center h-full min-h-[140px] group focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none"
                      >
                          <div className="w-9 h-9 rounded-full bg-white border border-blue-200 flex items-center justify-center mb-2 shadow-sm group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white transition-all text-blue-600">
                            <i className="fa-solid fa-plus text-sm" aria-hidden="true"></i>
                          </div>
                        <span className="text-xs font-extrabold text-blue-900 uppercase tracking-wide">Add Subject</span>
                        <span className="text-[11px] text-slate-500 mt-0.5">Organize your notes</span>
                      </button>
                    )}
                  </>
                )}
              </div>
            </section>

            {/* ── RECENT ACTIVITY (top 8) ── */}
            {!searching && (
              <section className="mb-10" aria-labelledby="recent-activity-heading">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2.5">
                    <span className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-bold shadow-xs">
                      <i className="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>
                    </span>
                    <div>
                      <h2 id="recent-activity-heading" className="text-base md:text-lg font-extrabold text-slate-900 tracking-tight">
                        Recent Activity
                      </h2>
                      <p className="text-xs text-slate-500">Pick up right where you left off</p>
                    </div>
                  </div>
                  <span className="text-xs text-slate-700 font-bold bg-slate-100 px-2.5 py-1 rounded-full">
                    Last 8 accessed
                  </span>
                </div>
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="h-48 bg-white rounded-xl border border-gray-100 animate-pulse shadow-sm" />
                    ))}
                  </div>
                ) : uniqueRecentNotes.length === 0 ? (
                  <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center shadow-xs">
                    <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xl mb-3">
                      <i className="fa-solid fa-book-open" aria-hidden="true"></i>
                    </div>
                    <h3 className="font-bold text-slate-800 text-base">No study sessions recorded yet</h3>
                    <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                      Select a note below or create a new reviewer note to start your study session.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {uniqueRecentNotes.slice(0, 8).map((note) => (
                      <NoteCard
                        key={note._id || note.id}
                        note={note}
                        folderName={folderMap[note.folder] || folderMap[note.folder?._id] || "Uncategorized"}
                        onStudy={handleStudyNavigation}
                        onRename={handleRenameNote}
                        onColorChange={handleColorChange}
                        onMove={openMoveModal}
                        onDelete={openDeleteModal}
                        onDownloadPdf={handleDownloadPdf}
                        onShare={openShareModal}
                        onArchive={handleArchiveNote}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* ── RECENT NOTES (up to 15) ── */}
            <section aria-labelledby="all-notes-heading">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold shadow-xs">
                    <i className="fa-solid fa-file-lines" aria-hidden="true"></i>
                  </span>
                  <div>
                    <h2 id="all-notes-heading" className="text-base md:text-lg font-extrabold text-slate-900 tracking-tight">
                      {searching ? 'Search Results' : 'Reviewer Notes'}
                    </h2>
                    <p className="text-xs text-slate-500">
                      {searching ? `Matching filter: "${q}"` : 'All created and shared study materials'}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2.5">
                  {/* Group By Folder Toggle Button */}
                  {!searching && (
                    <button
                      onClick={() => setGroupByFolder(!groupByFolder)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition flex items-center gap-1.5 ${
                        groupByFolder
                          ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                          : "bg-white text-slate-700 hover:bg-slate-100 border-slate-200"
                      }`}
                      title="Group notes by folder/subject"
                    >
                      <i className="fa-solid fa-layer-group text-xs"></i>
                      {groupByFolder ? "Grouped by Folder" : "Group by Folder"}
                    </button>
                  )}

                  {/* Inline Show/Hide Archived Toggle */}
                  <button
                    onClick={handleToggleShowArchived}
                    className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition flex items-center gap-1.5 ${
                      showArchived
                        ? "bg-amber-600 text-white border-amber-600 shadow-sm"
                        : "bg-white text-slate-700 hover:bg-slate-100 border-slate-200"
                    }`}
                  >
                    <i className={`fa-solid ${showArchived ? "fa-eye-slash" : "fa-box-archive"} text-xs`}></i>
                    {showArchived ? "Hide Archived" : "Show Archived"}
                  </button>

                  {!searching && recentNotes.length >= 15 && (
                    <button
                      onClick={() => navigate('/library/archive')}
                      aria-label="View archived notes"
                      className="text-xs text-blue-700 font-extrabold hover:text-blue-900 transition flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200"
                    >
                      <i className="fa-solid fa-box-archive text-xs" aria-hidden="true"></i> View Archive
                    </button>
                  )}
                </div>
              </div>

              {/* Filter Bar: Folder Chips */}
              {!searching && availableNoteFolders.length > 0 && (
                <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-3 text-xs">
                  <span className="text-slate-400 font-bold uppercase tracking-wider text-[11px] whitespace-nowrap">Filter:</span>
                  <button
                    onClick={() => setSelectedFolder("all")}
                    className={`px-3 py-1 rounded-full font-bold transition whitespace-nowrap ${
                      selectedFolder === "all"
                        ? "bg-indigo-600 text-white shadow-xs"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    All ({reviewerSectionNotes.length})
                  </button>
                  {availableNoteFolders.map((fName) => {
                    const count = reviewerSectionNotes.filter(
                      (n) => (folderMap[n.folder] || folderMap[n.folder?._id] || "Uncategorized") === fName
                    ).length;
                    return (
                      <button
                        key={fName}
                        onClick={() => setSelectedFolder(fName)}
                        className={`px-3 py-1 rounded-full font-bold transition whitespace-nowrap ${
                          selectedFolder === fName
                            ? "bg-indigo-600 text-white shadow-xs"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {fName} ({count})
                      </button>
                    );
                  })}
                </div>
              )}

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {[...Array(4)].map((_, i) => <div key={i} className="h-48 bg-white rounded-xl border border-gray-100 animate-pulse shadow-sm" />)}
                </div>
              ) : filteredNotes.length === 0 ? (
                <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center shadow-xs">
                   <div className="w-12 h-12 mx-auto rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-xl mb-3">
                     <i className="fa-solid fa-note-sticky" aria-hidden="true"></i>
                   </div>
                   <h3 className="font-extrabold text-slate-900 text-lg">
                     {recentNotes.length > 0 && !searching && selectedFolder === "all"
                       ? "All notes are in Recent Activity"
                       : "No notes found"}
                   </h3>
                   <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                     {searching
                       ? "No reviewers matched your query. Try clearing your search."
                       : recentNotes.length > 0 && selectedFolder === "all"
                       ? "Your accessed notes are displayed in the Recent Activity section above."
                       : "No notes match the selected filter."}
                   </p>
                </div>
              ) : groupByFolder && !searching ? (
                /* Grouped View */
                <div className="space-y-6">
                  {Object.entries(groupedNotes).map(([groupName, notesInGroup]) => {
                    const isCollapsed = Boolean(collapsedFolders[groupName]);
                    return (
                      <div key={groupName} className="bg-white/80 border border-slate-200/80 rounded-2xl p-4 md:p-5 shadow-xs transition">
                        <div
                          onClick={() => toggleFolderCollapse(groupName)}
                          className="flex items-center justify-between cursor-pointer select-none pb-2 border-b border-slate-100 mb-4 hover:opacity-80 transition"
                        >
                          <div className="flex items-center gap-2.5">
                            <i className="fa-solid fa-folder text-indigo-500"></i>
                            <h3 className="font-bold text-slate-800 text-base">{groupName}</h3>
                            <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full font-bold">
                              {notesInGroup.length}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="text-slate-400 hover:text-slate-600 p-1"
                            aria-label={isCollapsed ? "Expand group" : "Collapse group"}
                          >
                            <i className={`fa-solid ${isCollapsed ? "fa-chevron-right" : "fa-chevron-down"} text-xs`}></i>
                          </button>
                        </div>

                        {!isCollapsed && (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {notesInGroup.map((note) => (
                              <NoteCard
                                key={note._id || note.id}
                                note={note}
                                folderName={groupName}
                                onStudy={handleStudyNavigation}
                                onRename={handleRenameNote}
                                onColorChange={handleColorChange}
                                onMove={openMoveModal}
                                onDelete={openDeleteModal}
                                onDownloadPdf={handleDownloadPdf}
                                onShare={openShareModal}
                                onArchive={handleArchiveNote}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Flat Grid View */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredNotes.slice(0, 15).map((note) => (
                    <NoteCard 
                      key={note._id || note.id}
                      note={note}
                      folderName={folderMap[note.folder] || folderMap[note.folder?._id] || "Uncategorized"}
                      onStudy={handleStudyNavigation}
                      onRename={handleRenameNote}
                      onColorChange={handleColorChange}
                      onMove={openMoveModal}
                      onDelete={openDeleteModal}
                      onDownloadPdf={handleDownloadPdf}
                      onShare={openShareModal}
                      onArchive={handleArchiveNote}
                    />
                  ))}
                </div>
              )}

              {/* ── INLINE ARCHIVED NOTES SECTION ── */}
              {showArchived && (
                <div className="mt-10 pt-6 border-t-2 border-dashed border-amber-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2.5">
                      <span className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center text-sm font-bold shadow-xs">
                        <i className="fa-solid fa-box-archive" aria-hidden="true"></i>
                      </span>
                      <div>
                        <h3 className="text-base font-extrabold text-slate-900 tracking-tight">
                          Archived Notes
                        </h3>
                        <p className="text-xs text-slate-500">Preview and restore archived study materials</p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate('/library/archive')}
                      className="text-xs text-amber-700 font-bold hover:underline"
                    >
                      Open Full Archive Page →
                    </button>
                  </div>

                  {archivedLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {[...Array(2)].map((_, i) => (
                        <div key={i} className="h-48 bg-white rounded-xl border border-amber-100 animate-pulse shadow-sm" />
                      ))}
                    </div>
                  ) : archivedNotes.length === 0 ? (
                    <div className="bg-amber-50/50 border border-amber-200 rounded-2xl p-6 text-center text-sm text-slate-500">
                      No archived notes found.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {archivedNotes.map((note) => (
                        <NoteCard
                          key={note._id || note.id}
                          note={note}
                          folderName="Archived"
                          onStudy={handleStudyNavigation}
                          onRename={handleRenameNote}
                          onColorChange={handleColorChange}
                          onMove={openMoveModal}
                          onDelete={openDeleteModal}
                          onDownloadPdf={handleDownloadPdf}
                          onShare={openShareModal}
                          onUnarchive={handleUnarchiveNote}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>

      {/* Create Folder Modal */}
      {isModalOpen && <AddFolderModal onClose={() => setIsModalOpen(false)} onAdd={handleAddFolder} />}

      {/* --- RENAME FOLDER MODAL --- */}
      {isRenameFolderModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Rename Folder</h3>
              <span className="text-xs font-semibold text-gray-400">{renameFolderInput.length}/30</span>
            </div>
            
            <input 
              type="text" 
              autoFocus
              value={renameFolderInput}
              onChange={(e) => setRenameFolderInput(e.target.value)}
              maxLength={30}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition font-semibold text-gray-800 mb-6"
              placeholder="Folder Name"
              onKeyDown={(e) => e.key === 'Enter' && handleRenameFolderSubmit()}
            />

            <div className="flex gap-3">
              <button 
                onClick={() => setIsRenameFolderModalOpen(false)}
                className="flex-1 py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button 
                onClick={handleRenameFolderSubmit}
                className="flex-1 py-2.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 transition shadow-md hover:shadow-lg"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Move Note Modal --- */}
      {isMoveModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Move to Folder</h3>
            <p className="text-sm text-gray-500 mb-4">Select a destination for <b>{noteToMove?.title || noteToMove?.topic}</b></p>
            
            <div className="max-h-60 overflow-y-auto space-y-2 mb-4 pr-1">
              <button
                onClick={() => handleMoveSubmit(null)}
                className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
                  <i className="fa-solid fa-folder-minus"></i>
                </div>
                <span className="font-semibold text-gray-700">No Folder</span>
              </button>

              {folders.map(f => {
                const isActive = (noteToMove?.folder === f._id || noteToMove?.folder?._id === f._id);
                const theme = LIST_ITEM_VARIANTS[normalizeColorKey(f.color)] || LIST_ITEM_VARIANTS.blue;

                return (
                  <button
                    key={f._id}
                    onClick={() => handleMoveSubmit(f._id)}
                    className={`w-full text-left px-4 py-3 rounded-xl border transition flex items-center gap-3 ${
                      isActive 
                        ? `${theme.activeBorder} ${theme.activeBg} ring-1 ${theme.ring}` 
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme.iconBg} ${theme.iconText}`}>
                      <i className="fa-solid fa-folder"></i>
                    </div>
                    <span className="font-semibold text-gray-700 truncate">{f.name}</span>
                    {isActive && <i className={`fa-solid fa-check ${theme.iconText} ml-auto`}></i>}
                  </button>
                );
              })}
            </div>

            <button 
              onClick={() => setIsMoveModalOpen(false)}
              className="w-full py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* --- ✅ DELETE FOLDER MODAL --- */}
      {isDeleteFolderModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xs w-full p-6 animate-in zoom-in-95 duration-200 text-center">
            <div className="w-14 h-14 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
              <i className="fa-solid fa-folder-minus"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Folder?</h3>
            <p className="text-gray-500 text-sm mb-6">
              Are you sure you want to delete <b>{folderToDelete?.name}</b>?
              <br/><span className="text-xs text-red-500 mt-1 block font-semibold">Notes inside will be moved to "Uncategorized".</span>
            </p>
            
            <div className="flex flex-col gap-2">
              <button 
                onClick={handleDeleteFolderSubmit}
                className="w-full py-2.5 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 transition shadow-md hover:shadow-lg"
              >
                Yes, Delete it
              </button>
              <button 
                onClick={() => setIsDeleteFolderModalOpen(false)}
                className="w-full py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- DELETE NOTE MODAL --- */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xs w-full p-6 animate-in zoom-in-95 duration-200 text-center">
            <div className="w-14 h-14 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
              <i className="fa-solid fa-trash-can"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Note?</h3>
            <p className="text-gray-500 text-sm mb-6">
              Are you sure you want to delete <b>{noteToDelete?.title || noteToDelete?.topic}</b>? This cannot be undone.
            </p>
            
            <div className="flex flex-col gap-2">
              <button 
                onClick={handleDeleteSubmit}
                className="w-full py-2.5 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 transition shadow-md hover:shadow-lg"
              >
                Yes, Delete it
              </button>
              <button 
                onClick={() => setIsDeleteModalOpen(false)}
                className="w-full py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- SHARED CODE MODAL --- */}
      {isSharedCodeModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Enter Shared Code</h3>
            <p className="text-sm text-gray-500 mb-4">
              If your classmate shared content with you, paste the code below.
            </p>

            <input
              type="text"
              autoFocus
              disabled={isRedeemingCode}
              value={sharedCodeInput}
              onChange={(e) => setSharedCodeInput(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition font-semibold text-gray-800 mb-6 disabled:opacity-50"
              placeholder="e.g. ABCD-1234"
              onKeyDown={(e) => e.key === "Enter" && !isRedeemingCode && handleSharedCodeSubmit()}
            />

            <div className="flex gap-3">
              <button
                type="button"
                disabled={isRedeemingCode}
                onClick={() => {
                  setIsSharedCodeModalOpen(false);
                  setSharedCodeInput("");
                }}
                className="flex-1 py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isRedeemingCode || !sharedCodeInput.trim()}
                onClick={handleSharedCodeSubmit}
                className="flex-1 py-2.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 transition shadow-md hover:shadow-lg disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isRedeemingCode ? (
                  <>
                    <i className="fa-solid fa-circle-notch fa-spin text-xs"></i>
                    <span>Redeeming...</span>
                  </>
                ) : (
                  <span>Continue</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- SHARE MODAL --- */}
      {isShareModalOpen && noteToShare && (
        <ShareModal
          note={noteToShare}
          onClose={() => { setIsShareModalOpen(false); setNoteToShare(null); }}
          onUpdateNote={handleShareModalUpdateNote}
          onAlert={setAlert}
        />
      )}

      {/* Render SwiftAlert */}
      {alert && (
        <SwiftAlert 
          type={alert.type} 
          title={alert.title} 
          message={alert.message} 
          onClose={() => setAlert(null)} 
        />
      )}
    </div>
  );
};

export default Library;
